/**
 * ============================================================================
 *   Copyright (c) 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * ============================================================================
 *   Module       : Select
 *   Feature      : 197-portal-light-theme-responsive (design blueprint)
 *   Description  : Canonical token-styled <select> (with optional label/error),
 *                  matching the Input field. Replaces ad-hoc
 *                  `<select className="border border-gray-300 …">`.
 *   Blueprint    : docs/design-blueprint.md
 *   Constitution : v1.19.0
 *   Author       : Peter Dobler
 * ============================================================================
 */
import { forwardRef } from 'react';
import type { SelectHTMLAttributes } from 'react';
import { clsx } from 'clsx';

export interface SelectOption {
  value: string | number;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  selectSize?: 'sm' | 'md' | 'lg';
  label?: string;
  error?: boolean;
  errorMessage?: string;
  helperText?: string;
  isFullWidth?: boolean;
  /** Convenience: render options from data instead of children. */
  options?: SelectOption[];
}

const sizeStyles = { sm: 'text-sm px-3 py-1.5', md: 'text-sm px-4 py-2', lg: 'text-base px-4 py-2.5' } as const;

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    { selectSize = 'md', label, error, errorMessage, helperText, isFullWidth, options, children, className, id, ...props },
    ref,
  ) => {
    const selectId = id || `select-${Math.random().toString(36).slice(2, 9)}`;
    const isError = error || !!errorMessage;
    return (
      <div className={clsx('flex flex-col', isFullWidth && 'w-full')}>
        {label && (
          <label htmlFor={selectId} className="mb-1.5 text-sm font-medium text-[var(--color-text-primary)]">
            {label}
          </label>
        )}
        <select
          ref={ref}
          id={selectId}
          className={clsx(
            'block rounded-[var(--radius-md)] border bg-[var(--color-bg-card)] text-[var(--color-text-primary)]',
            'transition-colors duration-[120ms] focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            isError
              ? 'border-[var(--color-status-danger)] focus:border-[var(--color-status-danger)] focus:ring-[var(--color-status-danger)]'
              : 'border-[var(--color-border)] focus:border-[var(--color-accent)] focus:ring-[var(--color-accent)]',
            sizeStyles[selectSize],
            isFullWidth && 'w-full',
            className,
          )}
          {...props}
        >
          {options ? options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>) : children}
        </select>
        {(helperText || errorMessage) && (
          <p
            className={clsx(
              'mt-1.5 text-sm',
              errorMessage ? 'text-[var(--color-status-danger)]' : 'text-[var(--color-text-secondary)]',
            )}
          >
            {errorMessage || helperText}
          </p>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
